import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react"
import useDate, { Dayjs } from "~/ui-rtk/hooks/date"

const dayjs = useDate()

/**
 * Type definition for the cache entry.
 * Each cache entry contains:
 * - `createdAt`: Timestamp of when the cache was created.
 * - `data`: A `Set<string> | null` containing cached capabilities.
 * - `fillerId` (optional): ID of the process that is actively trying to fulfill this cache.
 */
type CapabilitiesCacheType = Record<
  string,
  { createdAt: Dayjs; data: Set<string> | null; fillerId?: number }
>

/**
 * Interface for the Capabilities Cache Context.
 * - `cache`: Stores cached capability data.
 * - `setCache`: Updates the cache with new data.
 * - `acquireIntent`: acquires intent to fulfill cache entry.
 * - `releaseIntent`: release intent to fulfill cache entry.
 */
interface CapabilitiesCacheContextType {
  cache: CapabilitiesCacheType
  setCache: (key: string, data: Set<string>) => void
  acquireIntent: (key: string, fillerId: number) => void
  releaseIntent: (key: string, fillerId: number) => void
}

const CapabilitiesCacheContext = createContext<
  CapabilitiesCacheContextType | undefined
>(undefined)

// Context Provider
export const CapabilitiesCacheProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [cache, setState] = useState<CapabilitiesCacheType>({})

  const setCache = useCallback(
    (key: string, data: Set<string>) => {
      setState(prevState => ({
        ...prevState,
        [key]: {
          createdAt: dayjs(),
          data,
        },
      }))
    },
    [setState],
  )

  // Claims the right to refresh an entry. It must NOT drop the data already
  // cached: every mounted cube query reads this Set, and blanking it makes the
  // capabilities gate skip every query app-wide until the refresh lands. Serving
  // the stale Set for the second it takes to refetch is strictly better.
  const acquireIntent = useCallback(
    (key: string, fillerId: number) => {
      setState(prevState => {
        if (prevState[key]?.fillerId != null) return prevState
        return {
          ...prevState,
          [key]: {
            createdAt: prevState[key]?.createdAt ?? dayjs(),
            data: prevState[key]?.data ?? null,
            fillerId,
          },
        }
      })
    },
    [setState],
  )

  const releaseIntent = useCallback(
    (key: string, fillerId: number) => {
      setState(prevState => {
        if (prevState[key]?.fillerId !== fillerId) return prevState
        return {
          ...prevState,
          [key]: {
            ...prevState[key],
            fillerId: undefined,
          },
        }
      })
    },
    [setState],
  )

  return (
    <CapabilitiesCacheContext.Provider
      value={{ cache, setCache, acquireIntent, releaseIntent }}
    >
      {children}
    </CapabilitiesCacheContext.Provider>
  )
}

export const useCapabilitiesCache = (key: string, expiryMinutes = 10) => {
  // kind of unique ID for the filler
  const fillerId = useRef(Math.floor(Math.random() * 1_000_000))
  const context = useContext(CapabilitiesCacheContext)

  if (!context) {
    throw new Error(
      "useCapabilitiesCache must be used within an CapabilitiesCacheProvider",
    )
  }

  const cacheEntry = context.cache[key]
  const isCacheValid =
    cacheEntry?.data != null &&
    dayjs().diff(cacheEntry.createdAt, "minutes") < expiryMinutes
  const isOwnedByAnother =
    cacheEntry?.fillerId != null && cacheEntry.fillerId !== fillerId.current

  // Claim the refresh whenever the entry is stale and unclaimed. This has to
  // react to the live cache state, not just run once on mount: an entry can
  // fall out of validity, or lose its filler when that component unmounts (or
  // when StrictMode replays this effect), long after every consumer has
  // mounted. With `[]` deps the condition was evaluated against the first
  // render's snapshot, so nobody re-claimed and the entry stayed unfilled —
  // which the capabilities gate reads as "no cubes available" and silently
  // skips every query.
  const { acquireIntent } = context
  useEffect(() => {
    if (!isCacheValid && !isOwnedByAnother) {
      acquireIntent(key, fillerId.current)
    }
  }, [key, isCacheValid, isOwnedByAnother, acquireIntent])

  // Release only on unmount/key change — not on every dependency change above,
  // which would release the claim we just took and loop.
  const { releaseIntent } = context
  useEffect(
    () => () => {
      releaseIntent(key, fillerId.current)
    },
    [key, releaseIntent],
  )

  const setCache = useCallback(
    (data: Set<string>) => {
      context.setCache(key, data)
    },
    [context.setCache, key],
  )

  return {
    data: cacheEntry?.data,
    setCache,
    // Allow to fulfill if: we own the fillerId AND (cache is invalid or empty)
    allowToFulfill: cacheEntry?.fillerId === fillerId.current && !isCacheValid,
  }
}
